import type { TFunction } from "i18next";
import { formatDate, formatDateTime } from "../helpers";

export interface AccessTableColumn {
  id: string;
  header: string;
  accessorKey: string;
  cell: (info: any) => any;
}

export const accessTableColumns = (t: TFunction): AccessTableColumn[] => [
  {
    id: "ID",
    header: "ID",
    accessorKey: "ID",
    cell: (info: any) => info.getValue()(),
  },
  {
    id: "name",
    header: t("name"),
    accessorKey: "name",
    cell: (info: any) => {
      const firstName = info.row.original?.firstName;
      const lastName = info.row.original?.lastName;
      if (firstName || lastName) {
        return `${firstName || ""} ${lastName || ""}`.trim();
      }
      return t("noData");
    },
  },
  {
    id: "type",
    header: t("accessType"),
    accessorKey: "type",
    cell: (info: any) => {
      return info.getValue() ? t(info.getValue()) : t("noData");
    },
  },
  {
    id: "status",
    header: t("status"),
    accessorKey: "status",
    cell: (info: any) => {
      return info.getValue() ? t(info.getValue()) : t("noData");
    },
  },
  {
    id: "isInsideYard",
    header: t("isInsideYard"),
    accessorKey: "isInsideYard",
    cell: (info: any) => {
      return info.getValue() ? t("yes") : t("no");
    },
  },
  {
    id: "lastEntry",
    header: t("lastEntry"),
    accessorKey: "lastEntry",
    cell: (info: any) => {
      const secondsUpdatedAt = info.getValue()?.updatedAt?._seconds;
      const nanosecondsUpdatedAt = info.getValue()?.updatedAt?._nanoseconds;
      return info.getValue()?.updatedAt
        ? `${formatDateTime(secondsUpdatedAt, nanosecondsUpdatedAt)}`
        : t("noData");
    },
  },
  {
    id: "lastExit",
    header: t("lastExit"),
    accessorKey: "lastExit",
    cell: (info: any) => {
      const secondsUpdatedAt = info.getValue()?.updatedAt?._seconds;
      const nanosecondsUpdatedAt = info.getValue()?.updatedAt?._nanoseconds;
      return info.getValue()?.updatedAt
        ? `${formatDateTime(secondsUpdatedAt, nanosecondsUpdatedAt)}`
        : t("noData");
    },
  },
  {
    id: "createdAt",
    header: t("created"),
    accessorKey: "createdAt",
    cell: (info: any) => {
      const seconds = info.getValue()?._seconds;
      const nanoseconds = info.getValue()?._nanoseconds;
      return formatDate(seconds, nanoseconds);
    },
  },
  {
    id: "updatedAt",
    header: t("updated"),
    accessorKey: "updatedAt",
    cell: (info: any) => {
      const seconds = info.getValue()?._seconds;
      const nanoseconds = info.getValue()?._nanoseconds;
      return formatDate(seconds, nanoseconds);
    },
  },
  {
    id: "history",
    header: t("history"),
    accessorKey: "history",
    cell: (info: any) => info.getValue()(),
  },
];

export interface AccessHistoryTableColumn {
  header: string;
  accessorKey: string;
  cell: (info: any) => any;
}

export interface AccessHistoryItemSelected {
  type: string;
  [key: string]: any;
}

export const accessHistoryTableColumns = (
  t: TFunction,
  itemSelected: AccessHistoryItemSelected
): AccessHistoryTableColumn[] => {
  let columns: AccessHistoryTableColumn[] = [
    {
      header: t("type"),
      accessorKey: "type",
      cell: (info: any) => {
        return info.getValue() ? t(info.getValue()) : t("noData");
      },
    },
    {
      header: t("accessType"),
      accessorKey: "accessType",
      cell: (info: any) => {
        return info.getValue() ? t(info.getValue()) : t("noData");
      },
    },
    {
      header: t("vehicleData"),
      accessorKey: "vehicleData",
      cell: (info: any) => {
        return info.getValue()
          ? `${t("type")}: ${
              info.getValue()?.vehicleType
                ? info.getValue()?.vehicleType
                : t("noData")
            } - ${t("plate")}: ${
              info.getValue()?.plate ? info.getValue().plate : t("noData")
            }\n${t("model")}: ${
              info.getValue()?.model ? info.getValue().model : t("noData")
            } - ${t("color")}: ${
              info.getValue()?.color ? info.getValue().color : t("noData")
            }`
          : t("notApplicable");
      },
    },
    {
      header: t("createdAt"),
      accessorKey: "createdAt",
      cell: (info: any) => {
        const seconds = info.getValue()?._seconds;
        const nanoseconds = info.getValue()?._nanoseconds;
        return info.getValue()
          ? formatDateTime(seconds, nanoseconds)
          : t("noData");
      },
    },
    {
      header: t("updatedAt"),
      accessorKey: "updatedAt",
      cell: (info: any) => {
        const seconds = info.getValue()?._seconds;
        const nanoseconds = info.getValue()?._nanoseconds;
        return info.getValue()
          ? formatDateTime(seconds, nanoseconds)
          : t("noData");
      },
    },
  ];

  if (itemSelected && itemSelected.type === "vehicle") {
    columns = columns.filter(
      (column) =>
        column.accessorKey !== "accessType" &&
        column.accessorKey !== "vehicleData"
    );
  }

  return columns;
};

interface Access {
  id: string;
  name: string;
  type: string;
  status: string;
  [key: string]: any;
}

export const AccessDetailsData = (data: Access, t: TFunction) => {
  const renderRole = (type: string) => {
    switch (type) {
      case "guard":
        return t("guard");
      case "admin":
        return t("admin");
      default:
        return t("N/A");
    }
  };

  const userDataColumn = {
    title: renderRole(data?.type),
    data: [
      {
        label: t("name"),
        value:
          data?.firstName && data.lastName
            ? `${data.firstName} ${data.lastName}`
            : t("N/A"),
      },
      {
        label: t("email"),
        value: data?.email ? data.email : t("N/A"),
      },
      {
        label: t("phone"),
        value: data?.phone
          ? `${data.phone.areaCodeNumber || ""} ${
              data.phone.number || ""
            }`.trim()
          : t("N/A"),
      },
    ],
  };

  const vehicleDataColumn = {
    title: t("vehicleDetails"),
    data: [
      {
        label: t("plate"),
        value: data?.isInsideYard
          ? data.lastEntry?.vehicleData?.plate || t("N/A")
          : data.lastExit?.vehicleData?.plate || t("N/A"),
      },
      {
        label: t("model"),
        value: data?.isInsideYard
          ? data.lastEntry?.vehicleData?.model || t("N/A")
          : data.lastExit?.vehicleData?.model || t("N/A"),
      },
      {
        label: t("color"),
        value: data?.isInsideYard
          ? data.lastEntry?.vehicleData?.color || t("N/A")
          : data.lastExit?.vehicleData?.color || t("N/A"),
      },
    ],
  };

  const columnAccessDataDithoutFromOrTo = {
    title: t("access"),
    data: [
      {
        label: t("accessType"),
        value: data?.isInsideYard
          ? t(data.lastEntry.accessType)
          : t(data.lastExit.accessType),
      },
      {
        label: t("status"),
        value: data?.status ? t(data.status) : t("N/A"),
      },
      {
        label: t("isInsideYard"),
        value: data?.isInsideYard ? t("yes") : t("no"),
      },
    ],
  };

  //Maximo 4 columnas
  return {
    title: t("accessDetails"),
    id: data?.id,
    columns: data?.isInsideYard
      ? data.lastEntry?.accessType === "vehicle"
        ? [userDataColumn, vehicleDataColumn, columnAccessDataDithoutFromOrTo]
        : [userDataColumn, columnAccessDataDithoutFromOrTo]
      : data.lastExit?.accessType === "vehicle"
      ? [userDataColumn, vehicleDataColumn, columnAccessDataDithoutFromOrTo]
      : [userDataColumn, columnAccessDataDithoutFromOrTo],
    itemSelected: data,
  };
};
